import { useState } from 'react';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  TextField,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation, useTranslationKeys } from '../common/components/LocalizationProvider';
import EditItemView from './components/EditItemView';
import { prefixString, unprefixString } from '../common/util/stringUtils';
import SelectField from '../common/components/SelectField';
import SettingsMenu from './components/SettingsMenu';
import { useCatch } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';

const NotificationPage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  const [item, setItem] = useState();

  const alarms = useTranslationKeys((it) => it.startsWith('alarm')).map((it) => ({
    key: unprefixString('alarm', it),
    name: t(it),
  }));

  const testNotificators = useCatch(async () => {
    await Promise.all(item.notificators.split(/[, ]+/).map(async (notificator) => {
      await fetchOrThrow(`/api/notifications/test/${notificator}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });
    }));
  });

  const validate = () =>
    item &&
    item.type &&
    item.notificators &&
    (!item.notificators?.includes('command') || item.commandId);

  return (
    <EditItemView
      endpoint="notifications"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedNotification']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="primary" />
                <Typography variant="subtitle1">
                  {t('sharedRequired')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <SelectField
                value={item?.type ?? ''}
                onChange={(e) => setItem({ ...item, type: e.target.value })}
                endpoint="/api/notifications/types"
                keyGetter={(it) => it.type}
                // 👇 prevenção contra erro charAt
                titleGetter={(it) =>
                  it?.type ? t(prefixString('event', it.type)) : ''
                }
                label={t('sharedType')}
                helperText={['geofenceEnter', 'geofenceExit'].includes(item.type) ? t('notificationGeofenceLabel') : null}
              />
              {item.type === 'alarm' && (
                <SelectField
                  multiple
                  value={
                    item.attributes && item.attributes.alarms
                      ? item.attributes.alarms.split(/[, ]+/)
                      : []
                  }
                  onChange={(e) =>
                    setItem({
                      ...item,
                      attributes: {
                        ...item.attributes,
                        alarms: e.target.value.join(),
                      },
                    })
                  }
                  data={alarms}
                  keyGetter={(it) => it.key}
                  label={t('sharedAlarms')}
                />
              )}
              <SelectField
                multiple
                value={
                  item.notificators
                    ? item.notificators.split(/[, ]+/)
                    : []
                }
                onChange={(e) =>
                  setItem({
                    ...item,
                    notificators: e.target.value.join(),
                  })
                }
                endpoint="/api/notifications/notificators"
                keyGetter={(it) => it.type}
                // 👇 também defensivo
                titleGetter={(it) =>
                  it?.type ? t(prefixString('notificator', it.type)) : ''
                }
                label={t('notificationNotificators')}
              />
              {item.notificators?.includes('command') && (
                <SelectField
                  value={item.commandId}
                  onChange={(e) =>
                    setItem({
                      ...item,
                      commandId: Number(e.target.value),
                    })
                  }
                  endpoint="/api/commands"
                  titleGetter={(it) => it.description}
                  label={t('sharedSavedCommand')}
                />
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={testNotificators}
                disabled={!item.notificators}
              >
                {t('sharedTestNotificators')}
              </Button>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!item.always}
                    onChange={(e) =>
                      setItem({ ...item, always: e.target.checked })
                    }
                  />
                }
                label={t('notificationAlways')}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <InfoIcon color="primary" />
                <Typography variant="subtitle1">
                  {t('sharedExtra')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <TextField
                value={item.description || ''}
                onChange={(e) =>
                  setItem({ ...item, description: e.target.value })
                }
                label={t('sharedDescription')}
              />
              <SelectField
                value={item.calendarId}
                onChange={(e) =>
                  setItem({
                    ...item,
                    calendarId: Number(e.target.value),
                  })
                }
                endpoint="/api/calendars"
                label={t('sharedCalendar')}
              />
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.attributes && item.attributes.priority}
                      onChange={(e) =>
                        setItem({
                          ...item,
                          attributes: {
                            ...item.attributes,
                            priority: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label={t('sharedPriority')}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </EditItemView>
  );
};

export default NotificationPage;
